import { createBrowserClient } from '@supabase/ssr'
import type { SupabaseClient } from '@supabase/supabase-js'

// These values are baked in at build time by Next.js
// They MUST be present during docker build via --build-arg
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''

// Singleton instance for browser
let browserClient: SupabaseClient | null = null

export function createClient(): SupabaseClient {
  // Server-side: return a dummy client that throws on use
  // This prevents crashes when the module is imported on the server
  // but ensures errors if actually used server-side
  if (typeof window === 'undefined') {
    const throwError = () => {
      throw new Error('Browser Supabase client cannot be used on server. Use createClient from @/lib/supabase/server instead.')
    }
    return {
      auth: {
        getUser: throwError,
        getSession: throwError,
        signIn: throwError,
        signOut: throwError,
        signUp: throwError,
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      },
      from: throwError,
      rpc: throwError,
      channel: throwError,
      removeChannel: throwError,
    } as unknown as SupabaseClient
  }

  // Browser-side: create or return singleton client
  if (!browserClient) {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error(
        'Missing NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY. ' +
        'These must be provided as build arguments during Docker build.'
      )
    }

    // Get cookie domain for SSO (extract parent domain from hostname)
    const hostname = window.location.hostname
    const cookieDomain = hostname.includes('futalisdev.cloud') ? '.futalisdev.cloud' : undefined

    browserClient = createBrowserClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      cookies: {
        // Custom cookie options for cross-subdomain SSO
        getAll() {
          return document.cookie.split(';').map(cookie => {
            const [name, ...rest] = cookie.trim().split('=')
            return { name, value: rest.join('=') }
          })
        },
        setAll(cookies) {
          cookies.forEach(({ name, value, options }) => {
            let cookieStr = `${name}=${value}`
            if (cookieDomain) {
              cookieStr += `; domain=${cookieDomain}`
            }
            cookieStr += '; path=/'
            if (options?.maxAge) {
              cookieStr += `; max-age=${options.maxAge}`
            }
            if (options?.sameSite) {
              cookieStr += `; samesite=${options.sameSite}`
            }
            if (window.location.protocol === 'https:') {
              cookieStr += '; secure'
            }
            document.cookie = cookieStr
          })
        },
      },
    })
  }

  return browserClient
}
